import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { LoginSupportHelper } from "@/components/auth/role-gate"
import TiktokConnectCta from "@/components/social-accounts/tiktok-connect-cta"
import TiktokIcon from "@/components/social-accounts/tiktok-icon"
import { TiktokOAuthStartForm } from "@/components/social-accounts/tiktok-oauth-start-form"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"
import { signupOauthResultPath, tiktokSignInPath } from "@/routes"

/**
 * Wall-clock backstop (ms) before a started-but-never-handed-off TikTok
 * sign-in gives up and routes to the shipped OKS-466 "couldn't connect to
 * TikTok" result page (OKS-487, TRD KD2 + KD3).
 *
 * A native top-level form POST's response is never observable in JS — the
 * browser either navigates away (success) or doesn't. So a generous timer
 * is the ONLY frontend-observable "the handoff never started" signal. It
 * is armed on submit and cancelled when the handoff actually COMMITS a
 * navigation away (`pagehide`, or `visibilitychange:hidden`), so a valid
 * handoff (which resolves in ~1s — the start POST does NO external HTTP, it
 * just mints a signed state + 303) cancels it well before fire. (We must
 * NOT cancel on `beforeunload`: it fires the instant the POST *initiates*
 * navigation — i.e. on every submit, before any response — which would kill
 * the backstop immediately and the recovery would never fire.)
 *
 * **Why navigate instead of an in-page error?** While a native top-level
 * POST navigation is *pending*, Chromium suppresses paint of the outgoing
 * document, so a `setState` recovery commits but never paints (verified in
 * real Chromium — TRD §8 R5). A fresh top-level GET via `window.location`
 * *supersedes* the stuck pending POST navigation and commits a new
 * document, which is why Option D works where in-place recovery can't.
 * (`window.stop()` was tried and empirically did NOT release the paint
 * suppression — TRD KD2.) 12s keeps the slow-but-valid-network interruption
 * window small (CTPO-accepted). Exported so unit tests can drive it with
 * fake timers.
 */
export const AMBASSADOR_CONNECT_SAFETY_TIMEOUT_MS = 12_000

export interface AmbassadorSignInPanelProps {
  /** Swap back to the role-select panel (client-side, no reload). */
  onBack: () => void
  /**
   * Override the safety-timeout window (ms). Defaults to
   * {@link AMBASSADOR_CONNECT_SAFETY_TIMEOUT_MS}. Tests pass a small value
   * with fake timers; production never sets it.
   */
  safetyTimeoutMs?: number
}

/**
 * The transient "Connecting to TikTok…" view (UX Spec §2 `214:267`).
 * Shown after the creator activates "Continue with TikTok" while the
 * browser hands off to TikTok's consent screen (top-level cross-origin
 * nav). Focus moves to the heading on mount and the region is an
 * `aria-live` status so screen readers announce it (UX Spec §6).
 *
 * **OKS-487:** this renders as a CHILD of the always-mounted
 * `<TiktokOAuthStartForm>` — it must NOT replace the form. See the panel
 * doc below for why unmounting the form strands the spinner.
 */
function ConnectingState() {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="ambassador-connecting"
      className="flex flex-col items-center gap-4 py-6 text-center"
    >
      <span
        aria-hidden="true"
        className="flex size-16 items-center justify-center rounded-xl bg-black"
      >
        <TiktokIcon width={28} height={28} />
      </span>
      <Spinner className="size-5" />
      <div className="space-y-1">
        <p
          ref={headingRef}
          tabIndex={-1}
          className="text-foreground font-medium outline-none"
        >
          {t("login.ambassador.connecting_title")}
        </p>
        <p className="text-muted-foreground text-sm">
          {t("login.ambassador.connecting_body")}
        </p>
      </div>
    </div>
  )
}

/**
 * AmbassadorSignInPanel — OKS-475 Ambassador branch (creators).
 *
 * Renders "Continue with TikTok", which POSTs natively (NOT Inertia XHR)
 * to `tiktokSignInPath()` (`POST /sign_in/tiktok`) via
 * `TiktokOAuthStartForm` — TikTok OAuth requires a top-level cross-origin
 * browser nav. The shared callback then signs in an existing account (→
 * /dashboard) or routes an unknown TikTok to the OKS-466 OAuth-result "no
 * crew account" dead-end. No account is created here (signup is
 * invite-only, OKS-466).
 *
 * **OKS-487 fix.** The `<TiktokOAuthStartForm>` is ALWAYS mounted — the
 * Connecting state renders as its *children*, never as a replacement.
 * Previously the panel early-returned `<ConnectingState/>` on submit,
 * which unmounted the `<form>` synchronously inside the submit handler.
 * Per the HTML form-submission algorithm a form that is "not connected"
 * cannot navigate, so the browser aborted the POST and the spinner hung
 * forever (the bug). Keeping the form connected lets the native POST
 * navigate.
 *
 * **Recovery (Option D, CTPO-approved).** If the handoff never resolves
 * within {@link AMBASSADOR_CONNECT_SAFETY_TIMEOUT_MS}, the panel routes to
 * the shipped OKS-466 "couldn't connect to TikTok" result page via
 * `window.location.assign(signupOauthResultPath({ reason: "error" }))` — a
 * fresh top-level GET that supersedes the stalled pending POST navigation,
 * escaping the frozen spinner (PRD §7 pre-sanctioned the OKS-466 result
 * page for start failures). A valid handoff cancels the timer ~1s in, so
 * this only fires for a genuinely stuck start.
 */
export default function AmbassadorSignInPanel({
  onBack,
  safetyTimeoutMs = AMBASSADOR_CONNECT_SAFETY_TIMEOUT_MS,
}: AmbassadorSignInPanelProps) {
  const { t } = useTranslation()
  const [connecting, setConnecting] = useState(false)

  // Holds the live safety-timeout id so it can be RE-ARMED across visibility
  // transitions (OKS-490 KD2). A native top-level POST's response is never
  // observable in JS, so this wall-clock timer is the only frontend signal
  // for "the handoff never started". On fire it routes to the OKS-466 result
  // page via a fresh top-level GET, which supersedes the stuck pending POST
  // nav (an in-page paint is suppressed while the POST is pending — TRD
  // KD2/R5). Kept in a ref (not effect-closure scope) so `arm()`/`cancel()`
  // operate on one shared id — structurally never more than one live timer,
  // so recovery fires at most once.
  const safetyTimer = useRef<number | undefined>(undefined)

  // Arm the safety-timeout backstop while connecting; cancel it when the
  // handoff actually COMMITS a navigation away (`pagehide`, or
  // `visibilitychange:hidden`), and clean up on unmount / state change.
  //
  // **OKS-490 re-arm.** A creator who tab-switches DURING a genuinely hung
  // handoff triggers `visibilitychange:hidden` → `cancel()`, standing the net
  // down. Previously nothing re-armed it on return, so the creator was
  // permanently stranded on the spinner. Now `visibilitychange:visible` while
  // still `connecting` calls `arm()` for a FRESH full window (KD1), so a hung
  // handoff always recovers. This can never affect the happy path: once a real
  // nav commits, the document is gone and `visible` can't fire on it (KD4).
  // `arm()` always `cancel()`s first, so a subsequent real handoff (`pagehide`
  // or `hidden`) still cancels the re-armed timer with no leak.
  useEffect(() => {
    if (!connecting) return undefined

    const cancel = () => {
      if (safetyTimer.current !== undefined) {
        window.clearTimeout(safetyTimer.current)
        safetyTimer.current = undefined
      }
    }
    const arm = () => {
      cancel()
      safetyTimer.current = window.setTimeout(() => {
        window.location.assign(signupOauthResultPath({ reason: "error" }))
      }, safetyTimeoutMs)
    }

    // Cancel on a real navigation COMMIT: `pagehide` (document unload) or
    // `visibilitychange:hidden` (a secondary real-handoff / tab-away signal).
    // On `visibilitychange:visible` while still connecting, RE-ARM a fresh
    // window (OKS-490). We deliberately do NOT listen for `beforeunload`: it
    // fires the instant the form POST *initiates* navigation (i.e. on every
    // submit, before any response), so it would kill the backstop immediately
    // on click and the recovery would never fire.
    const onVisibility = () => {
      if (document.visibilityState === "hidden") cancel()
      else arm()
    }

    arm()
    window.addEventListener("pagehide", cancel)
    document.addEventListener("visibilitychange", onVisibility)

    return () => {
      cancel()
      window.removeEventListener("pagehide", cancel)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [connecting, safetyTimeoutMs])

  // bfcache restore (creator hit "back" from TikTok): never restore a
  // frozen spinner — reset to the default panel so the CTA is usable.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setConnecting(false)
    }
    window.addEventListener("pageshow", onPageShow)
    return () => window.removeEventListener("pageshow", onPageShow)
  }, [])

  // Native submit: do NOT preventDefault — the top-level POST must proceed.
  // Flip to Connecting; the effect above arms the backstop. While connecting
  // the CTA is replaced by the spinner, so a second submit can't fire
  // (single in-flight — KD5).
  const handleSubmit = () => {
    setConnecting(true)
  }

  return (
    <div className="flex flex-col gap-6" data-testid="login-ambassador-panel">
      <div className="space-y-1 text-center">
        <h1 className="text-xl font-medium">{t("login.ambassador.title")}</h1>
        {!connecting && (
          <p className="text-muted-foreground text-sm">
            {/* Mobile gets the longer "it's how your crew posts" sub; the
                terser desktop sub appears ≥ md. Both always render — only
                visibility toggles (UX Spec §5: nothing hides between
                breakpoints structurally). */}
            <span className="md:hidden">{t("login.ambassador.subtitle")}</span>
            <span className="hidden md:inline">
              {t("login.ambassador.subtitle_desktop")}
            </span>
          </p>
        )}
      </div>

      {/* The form is ALWAYS mounted (OKS-487) — only its children swap
          between the CTA and the Connecting state, so React reuses the same
          <form> DOM node and the native POST can navigate. */}
      <TiktokOAuthStartForm
        action={tiktokSignInPath()}
        className={cn("w-full", connecting && "pointer-events-none")}
        data-testid="ambassador-tiktok-form"
        onSubmit={handleSubmit}
      >
        {connecting ? (
          <ConnectingState />
        ) : (
          <TiktokConnectCta data-testid="ambassador-tiktok-cta" />
        )}
      </TiktokOAuthStartForm>

      <LoginSupportHelper />

      <button
        type="button"
        onClick={onBack}
        className="text-muted-foreground hover:text-foreground text-center text-sm"
        data-testid="login-ambassador-back"
      >
        {t("login.ambassador.back")}
      </button>
    </div>
  )
}
